/**
 * DecorCatalogModal.ts
 * Milestone M4: Furniture Store, Upgrade Manager & State Souvenir Display Modal.
 */

import { DecorSystem, FURNITURE_CATALOG_ITEMS } from '../../systems/DecorSystem';
import { GameState } from '../../core/GameState';
import { EventBus } from '../../core/EventBus';
import { ToastManager } from '../ToastManager';
import {
  getAllSouvenirEntries,
  getSouvenirEntry,
  isSouvenirPlaced,
  isSouvenirUnlocked,
} from '../../content/StateSouvenirsRegistry';
import { PlacedFurniture } from '../../core/Types';
import {
  renderDecorFurnitureVisual,
  renderSouvenirVisual,
  renderUiGouacheIcon,
} from '../Icons';
import {
  cancelPendingScrollRestores,
  preserveScrollPositions,
  preventButtonFocusScroll
} from '../ScrollPreserver';

export class DecorCatalogModal {
  private static instance: DecorCatalogModal;
  private modalContainer: HTMLElement | null = null;
  private modalPortal: HTMLElement | null = null;
  private activeTab: 'placed' | 'shop' | 'souvenirs' = 'shop';
  private selectedShopCategory: 'ALL' | 'BED' | 'FRIDGE' | 'DESK' | 'SOFA' | 'PLANT' = 'ALL';
  private focusedFurnitureId: string | null = null;

  private constructor() {
    if (typeof document !== 'undefined') {
      this.modalContainer = document.getElementById('modal-container');
      this.modalPortal = document.getElementById('modal-portal');
    }
  }

  public static getInstance(): DecorCatalogModal {
    if (!DecorCatalogModal.instance) {
      DecorCatalogModal.instance = new DecorCatalogModal();
    }
    return DecorCatalogModal.instance;
  }

  public open(focusedId?: string, initialTab?: 'placed' | 'shop' | 'souvenirs'): void {
    this.focusedFurnitureId = focusedId || null;
    this.activeTab = initialTab || (focusedId ? 'placed' : 'shop');
    if (typeof document !== 'undefined') {
      this.modalContainer = document.getElementById('modal-container') || this.modalContainer;
      this.modalPortal = document.getElementById('modal-portal') || this.modalPortal;
    }
    this.render(false);
  }

  public close(): void {
    cancelPendingScrollRestores();
    const container =
      (typeof document !== 'undefined' ? document.getElementById('modal-container') : null) ||
      this.modalContainer;
    const portal =
      (typeof document !== 'undefined' ? document.getElementById('modal-portal') : null) ||
      this.modalPortal;
    if (container) {
      container.classList.remove('active');
      container.setAttribute('aria-hidden', 'true');
    }
    if (portal) {
      portal.innerHTML = '';
    }
  }

  private render(isSteadyRerender: boolean = true): void {
    if (typeof document !== 'undefined') {
      this.modalContainer = document.getElementById('modal-container') || this.modalContainer;
      this.modalPortal = document.getElementById('modal-portal') || this.modalPortal;
    }
    if (!this.modalContainer || !this.modalPortal) return;

    const state = GameState.getState();
    const money = state.stats.money;
    const passive = DecorSystem.calculatePassiveBuffs();
    const container = this.modalContainer;
    const portal = this.modalPortal;

    preserveScrollPositions(portal, () => {
      portal.innerHTML = `
        <div class="decor-catalog-card ui-modal-card ui-modal-card--parchment" style="
          max-width: 374px;
          gap: 12px;
        ">
          <!-- Header -->
          <div class="ui-modal-header">
            <div>
              <h3 class="ui-modal-title" style="color: #3B2314; display: flex; align-items: center; gap: 7px;">
                ${renderUiGouacheIcon('icon_decor_palette', { size: 22, className: 'decor-modal-hdr-icon' })}
                <span>Nội Thất & Xây Dựng Phòng 2.5D</span>
              </h3>
              <p class="ui-modal-subtitle" style="color: #6B4732; font-weight: 700;">
                Số dư: <strong class="tabular-nums" style="color: #246847; font-weight: 900;">$${Math.floor(money)} USD</strong>
              </p>
            </div>
            <button type="button" id="decor-modal-close" class="ui-modal-close">✕</button>
          </div>

          <!-- Passive Room Buff Summary -->
          <div style="
            background: linear-gradient(180deg, #FFFDF7 0%, #F7E8C9 100%);
            border: 1.8px solid #3B2314;
            box-shadow: 0 2px 0 #3B2314, inset 0 1.5px 0 #FFFFFF;
            border-radius: 12px;
            padding: 8px 11px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 11px;
            gap: 6px;
          ">
            <span style="color: #3B2314; font-weight: 800; display: inline-flex; align-items: center; gap: 5px;">
              ${renderUiGouacheIcon('icon_home_cottage', { size: 15 })}
              <span>Hồi phục thụ động:</span>
            </span>
            <div class="tabular-nums" style="display: flex; gap: 8px; font-weight: 900;">
              <span style="color: #7C471C; display: inline-flex; align-items: center; gap: 3px;">
                ${renderUiGouacheIcon('icon_energy_bolt', { size: 14 })} +${passive.passiveEnergyPerMin}%/p
              </span>
              <span style="color: #246847; display: inline-flex; align-items: center; gap: 3px;">
                ${renderUiGouacheIcon('icon_mood_happy', { size: 14 })} +${passive.passiveHappinessPerMin}%/p
              </span>
            </div>
          </div>

          <!-- Tabs -->
          <div class="ui-segmented-tabs">
            <button type="button" id="decor-tab-placed" class="ui-tab-btn tabular-nums ${this.activeTab === 'placed' ? 'active active-purple' : ''}" style="font-size: 11px; padding: 7px 4px; display: inline-flex; align-items: center; justify-content: center; gap: 4px;">
              ${renderUiGouacheIcon('icon_home_cottage', { size: 15 })}
              <span>Trong phòng (${state.placedFurniture.length})</span>
            </button>
            <button type="button" id="decor-tab-shop" class="ui-tab-btn ${this.activeTab === 'shop' ? 'active active-purple' : ''}" style="font-size: 11px; padding: 7px 4px; display: inline-flex; align-items: center; justify-content: center; gap: 4px;">
              ${renderUiGouacheIcon('icon_shop_basket', { size: 15 })}
              <span>Mua thêm</span>
            </button>
            <button type="button" id="decor-tab-souvenirs" class="ui-tab-btn ${this.activeTab === 'souvenirs' ? 'active active-purple' : ''}" style="font-size: 11px; padding: 7px 4px; display: inline-flex; align-items: center; justify-content: center; gap: 4px;">
              ${renderUiGouacheIcon('icon_trophy_medal', { size: 15 })}
              <span>Kỷ vật Bang</span>
            </button>
          </div>

          <!-- Body -->
          <div style="display: flex; flex-direction: column; gap: 8px;">
            ${this.renderTabBody()}
          </div>
        </div>
      `;

      container.classList.add('active');
      container.setAttribute('aria-hidden', 'false');
      this.bindEvents();
      preventButtonFocusScroll(portal);
    }, { isFreshModalOpen: !isSteadyRerender });
  }

  private renderTabBody(): string {
    const state = GameState.getState();

    if (this.activeTab === 'placed') {
      return state.placedFurniture
        .map((item: PlacedFurniture) => {
          const isFocused = item.id === this.focusedFurnitureId;
          const catalogNow = DecorSystem.getCatalogItem(item.typeId, item.tier || 1);
          const nextCatalog = DecorSystem.getCatalogItem(item.typeId, (item.tier || 1) + 1);
          const isSouvenir = item.typeId === 'souvenir';

          let title = catalogNow?.vietnameseName || item.typeId;
          let utilityDesc = catalogNow?.activeUtilityDescription;
          let iconVisual = renderDecorFurnitureVisual(item.typeId, item.tier || 1, {
            size: 38,
            idSuffix: item.id,
            title,
          });
          if (isSouvenir) {
            const rawSouvId = item.customData?.souvenirId || 'souvenir_ny_statue_liberty';
            const souvEntry = getSouvenirEntry(rawSouvId);
            title = souvEntry ? `Kỷ vật: ${souvEntry.name}` : 'Kỷ vật Tiểu bang';
            utilityDesc = souvEntry?.buff.description;
            iconVisual = renderSouvenirVisual(rawSouvId, {
              size: 38,
              idSuffix: item.id,
              title,
            });
          }

          return `
            <div class="ui-item-card" style="
              background: ${isFocused ? 'linear-gradient(180deg, #FFF3CC 0%, #F7DF9C 100%)' : '#FFFDF7'};
              border: 1.8px solid #3B2314;
              box-shadow: 0 2.5px 0 #3B2314;
            ">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div style="display: flex; align-items: center; gap: 10px;">
                  <span class="${isSouvenir ? 'souvenir-catalog-pedestal-thumb is-unlocked' : ''}" style="display: inline-flex; align-items: center; justify-content: center; width: 46px; height: 46px; border-radius: 11px; background: #FFF5E1; border: 1.8px solid #3B2314; box-shadow: inset 0 1.5px 0 #FFFFFF, 0 1.5px 0 #3B2314; flex-shrink: 0;">${iconVisual}</span>
                  <div>
                    <div style="font-size: 12px; font-weight: 800; color: #3B2314;">
                      ${title} ${!isSouvenir ? `<span class="tabular-nums" style="color: #8C4716; font-size: 10px; font-weight: 900;">(Tier ${item.tier})</span>` : ''}
                    </div>
                    <div class="tabular-nums" style="font-size: 10px; color: #6B4732; font-weight: 700;">
                      Ô lưới: (${item.gridCol}, ${item.gridRow})
                    </div>
                    ${utilityDesc ? `
                      <div style="font-size: 10px; color: #7C471C; font-weight: 700; margin-top: 2px; display: inline-flex; align-items: center; gap: 3px;">
                        ${renderUiGouacheIcon('icon_energy_bolt', { size: 12 })}
                        <span>${utilityDesc}</span>
                      </div>
                    ` : ''}
                  </div>
                </div>
              </div>

              <div style="display: flex; gap: 6px; flex-wrap: wrap;">
                ${nextCatalog ? `
                  <button type="button" class="btn-upgrade-item tabular-nums" data-id="${item.id}" style="
                    flex: 1; background: linear-gradient(180deg, #C7EED7 0%, #81B29A 100%);
                    border: 1.8px solid #3B2314; box-shadow: 0 2.5px 0 #3B2314, inset 0 1px 0 rgba(255,255,255,0.75);
                    border-radius: 10px; padding: 7px 10px; color: #1F120A;
                    font-size: 10.5px; font-weight: 900; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 4px;
                  ">
                    ${renderUiGouacheIcon('icon_trophy_medal', { size: 13 })}
                    <span>Nâng lên T${nextCatalog.tier}: ${nextCatalog.vietnameseName} ($${nextCatalog.costUSD})</span>
                  </button>
                ` : `
                  <span style="flex: 1; font-size: 10.5px; color: #246847; font-weight: 800; padding: 4px 0; display: inline-flex; align-items: center; gap: 4px;">
                    ${renderUiGouacheIcon('icon_check_seal', { size: 13 })}
                    <span>Đã đạt cấp độ tối đa</span>
                  </span>
                `}

                ${(item.typeId === 'plant' || item.typeId === 'souvenir') ? `
                  <button type="button" class="btn-remove-item" data-id="${item.id}" style="
                    background: linear-gradient(180deg, #FAD4C4 0%, #EC9A80 100%); border: 1.8px solid #3B2314;
                    box-shadow: 0 2.5px 0 #3B2314, inset 0 1px 0 rgba(255,255,255,0.7);
                    border-radius: 10px; padding: 6px 10px; color: #2D160D; font-size: 10.5px; font-weight: 900; cursor: pointer;
                    display: inline-flex; align-items: center; gap: 4px;
                  ">
                    ${renderUiGouacheIcon('icon_storage_box', { size: 13 })}
                    <span>Cất vào kho</span>
                  </button>
                ` : ''}
              </div>
            </div>
          `;
        })
        .join('');
    }

    if (this.activeTab === 'shop') {
      const categories: Array<{ id: 'ALL' | 'BED' | 'FRIDGE' | 'DESK' | 'SOFA' | 'PLANT'; label: string; icon: string }> = [
        { id: 'ALL', label: 'Tất cả', icon: renderUiGouacheIcon('icon_shop_basket', { size: 15 }) },
        { id: 'BED', label: 'Giường', icon: renderDecorFurnitureVisual('bed', 1, { size: 16, idSuffix: 'shop_cat_bed' }) },
        { id: 'FRIDGE', label: 'Tủ lạnh', icon: renderDecorFurnitureVisual('fridge', 1, { size: 16, idSuffix: 'shop_cat_fridge' }) },
        { id: 'DESK', label: 'Bàn PC', icon: renderDecorFurnitureVisual('desk', 1, { size: 16, idSuffix: 'shop_cat_desk' }) },
        { id: 'SOFA', label: 'Sofa', icon: renderDecorFurnitureVisual('sofa', 1, { size: 16, idSuffix: 'shop_cat_sofa' }) },
        { id: 'PLANT', label: 'Cây', icon: renderDecorFurnitureVisual('plant', 1, { size: 16, idSuffix: 'shop_cat_plant' }) },
      ];

      const filterBarHtml = `
        <div id="decor-shop-filters" class="decor-shop-filter-bar" role="tablist" aria-label="Lọc danh mục nội thất">
          ${categories
            .map(
              (c) => `
              <button
                type="button"
                class="btn-shop-filter ${this.selectedShopCategory === c.id ? 'active' : ''}"
                data-category="${c.id}"
                aria-pressed="${this.selectedShopCategory === c.id ? 'true' : 'false'}"
                style="display: inline-flex; align-items: center; gap: 4px;"
              >
                ${c.icon} ${c.label}
              </button>
            `
            )
            .join('')}
        </div>
      `;

      const buyable = FURNITURE_CATALOG_ITEMS.filter((item) => {
        if (this.selectedShopCategory !== 'ALL' && item.category !== this.selectedShopCategory) {
          return false;
        }
        return true;
      });

      const itemsHtml = buyable
        .map((item) => {
          const placedCount = state.placedFurniture.filter(
            (f) => f.typeId === item.typeId && (f.tier || 1) === item.tier
          ).length;

          return `
            <div class="ui-item-card" style="
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
              gap: 10px;
              background: #FFFDF7;
              border: 1.8px solid #3B2314;
              box-shadow: 0 2.5px 0 #3B2314;
            ">
              <div style="display: flex; align-items: center; gap: 10px; min-width: 0;">
                <span style="display: inline-flex; align-items: center; justify-content: center; width: 52px; height: 52px; border-radius: 12px; background: #FFF5E1; border: 1.8px solid #3B2314; box-shadow: inset 0 1.5px 0 #FFFFFF, 0 1.5px 0 #3B2314; flex-shrink: 0;">
                  ${renderDecorFurnitureVisual(item.typeId, item.tier, { size: 44, idSuffix: `shop_${item.typeId}_${item.tier}`, title: item.vietnameseName })}
                </span>
                <div style="min-width: 0;">
                  <div style="font-size: 12px; font-weight: 800; color: #3B2314; display: flex; align-items: center; gap: 5px; flex-wrap: wrap;">
                    <span>${item.vietnameseName}</span>
                    <span class="tabular-nums" style="font-size: 10px; color: #8C4716; font-weight: 900;">(T${item.tier})</span>
                    ${
                      placedCount > 0
                        ? `<span class="tabular-nums" style="font-size: 9px; color: #1F4E37; font-weight: 900; background: #D7F2E3; border: 1.4px solid #3B2314; border-radius: 999px; padding: 1px 6px;">Đang có: ${placedCount}</span>`
                        : ''
                    }
                  </div>
                  <div class="tabular-nums" style="font-size: 10px; color: #6B4732; font-weight: 700; margin-top: 2px;">
                    Kích thước: ${item.width}x${item.height} • NL +${item.passiveEnergyBuff}%/p • Vui +${item.passiveHappinessBuff}%/p
                  </div>
                  ${item.activeUtilityDescription ? `
                    <div style="font-size: 10px; color: #7C471C; font-weight: 700; margin-top: 2px; display: inline-flex; align-items: center; gap: 3px;">
                      ${renderUiGouacheIcon('icon_energy_bolt', { size: 12 })}
                      <span>${item.activeUtilityDescription}</span>
                    </div>
                  ` : ''}
                </div>
              </div>
              <button type="button" class="btn-buy-furniture tabular-nums" data-type="${item.typeId}" data-tier="${item.tier}" style="
                background: linear-gradient(180deg, #C7EED7 0%, #81B29A 100%);
                border: 2px solid #3B2314;
                box-shadow: 0 2.5px 0 #3B2314, inset 0 1.5px 0 rgba(255, 255, 255, 0.75);
                border-radius: 11px;
                padding: 7px 12px;
                color: #1F120A;
                font-size: 11px;
                font-weight: 900;
                cursor: pointer;
                white-space: nowrap;
                flex-shrink: 0;
              ">
                Mua $${item.costUSD}
              </button>
            </div>
          `;
        })
        .join('');


      return `${filterBarHtml}${itemsHtml}`;
    }

    // Tab: State Souvenirs (All 15 bespoke 2.5D Gouache State & Regional Souvenirs)
    const allSouvenirs = [...getAllSouvenirEntries()].sort((a, b) => {
      if (a.isFlagship !== b.isFlagship) return a.isFlagship ? -1 : 1;
      const aUnlocked = isSouvenirUnlocked(a.id, state.inventory.unlockedSouvenirs);
      const bUnlocked = isSouvenirUnlocked(b.id, state.inventory.unlockedSouvenirs);
      if (aUnlocked !== bUnlocked) return aUnlocked ? -1 : 1;
      return 0;
    });

    return allSouvenirs
      .map((entry) => {
        const unlocked = isSouvenirUnlocked(entry.id, state.inventory.unlockedSouvenirs);
        const placed = isSouvenirPlaced(entry.id, state.placedFurniture);
        const stateBadge =
          entry.coveredStateCodes.length === 1
            ? entry.primaryStateCode
            : `${entry.primaryStateCode} +${entry.coveredStateCodes.length - 1}`;

        return `
          <div class="ui-item-card" style="
            background: ${unlocked ? '#FFFDF7' : '#F2E7D3'};
            border: 1.8px solid #3B2314;
            box-shadow: 0 2.5px 0 #3B2314;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            gap: 10px;
          ">
            <div style="display: flex; align-items: center; gap: 10px;">
              <span class="souvenir-catalog-pedestal-thumb ${unlocked ? 'is-unlocked' : 'is-locked'}" style="display: inline-flex; align-items: center; justify-content: center; width: 46px; height: 46px; border-radius: 11px; background: #FFF5E1; border: 1.8px solid #3B2314; flex-shrink: 0;">
                ${renderSouvenirVisual(entry.id, { size: 38, idSuffix: `cat_${entry.id}`, title: entry.name })}
              </span>
              <div>
                <div style="font-size: 12px; font-weight: 800; color: ${unlocked ? '#3B2314' : '#7A6352'};">
                  ${entry.name} (${stateBadge})
                </div>
                <div style="font-size: 10px; color: #6B4732; font-weight: 700; margin-top: 2px;">
                  ${entry.buff.description}
                </div>
              </div>
            </div>
            ${
              placed
                ? `<span style="font-size: 10.5px; color: #246847; font-weight: 900; white-space: nowrap; display: inline-flex; align-items: center; gap: 3px;">${renderUiGouacheIcon('icon_check_seal', { size: 13 })}<span>Đang trưng bày</span></span>`
                : unlocked
                ? `<button type="button" class="btn-place-souvenir" data-souvenir="${entry.id}" style="
                    background: linear-gradient(180deg, #FFF1BF 0%, #F4C36C 100%); border: 1.8px solid #3B2314;
                    box-shadow: 0 2.5px 0 #3B2314, inset 0 1px 0 rgba(255,255,255,0.75);
                    border-radius: 10px; padding: 6px 10px; color: #3B2314; font-size: 11px; font-weight: 900; cursor: pointer; white-space: nowrap;
                  ">Đặt vào phòng</button>`
                : `<span style="font-size: 10px; color: #6B4732; font-weight: 800; white-space: nowrap; display: inline-flex; align-items: center; gap: 3px;">${renderUiGouacheIcon('icon_lock_padlock', { size: 13 })}<span>Mở khóa ${entry.primaryStateName}</span></span>`
            }
          </div>
        `;
      })
      .join('');
  }

  private bindEvents(): void {
    document.getElementById('decor-modal-close')?.addEventListener('click', () => {
      this.close();
    });

    document.getElementById('decor-tab-placed')?.addEventListener('click', () => {
      this.activeTab = 'placed';
      this.render();
    });

    document.getElementById('decor-tab-shop')?.addEventListener('click', () => {
      this.activeTab = 'shop';
      this.render();
    });

    document.getElementById('decor-tab-souvenirs')?.addEventListener('click', () => {
      this.activeTab = 'souvenirs';
      this.render();
    });

    // Filter category pills inside Shop tab
    document.querySelectorAll('.btn-shop-filter').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const cat = (e.currentTarget as HTMLElement).getAttribute('data-category') as
          | 'ALL'
          | 'BED'
          | 'FRIDGE'
          | 'DESK'
          | 'SOFA'
          | 'PLANT';
        if (cat) {
          this.selectedShopCategory = cat;
          this.render();
        }
      });
    });

    // Upgrade item
    document.querySelectorAll('.btn-upgrade-item').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id')!;
        const res = DecorSystem.upgradeFurniture(id);
        if (res.success) {
          ToastManager.getInstance().show(`Nâng cấp thành công lên Tier ${res.newTier}!`, 'success');
          this.render();
        } else {
          ToastManager.getInstance().show(res.error || 'Không thể nâng cấp!', 'warning');
        }
      });
    });

    // Remove item
    document.querySelectorAll('.btn-remove-item').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id')!;
        const res = DecorSystem.removeFurniture(id);
        if (res.success) {
          ToastManager.getInstance().show('Đã cất đồ trang trí khỏi phòng!', 'info');
          this.render();
        } else {
          ToastManager.getInstance().show(res.error || 'Không thể cất!', 'warning');
        }
      });
    });

    // Buy new furniture -> immediately enter Move Mode on the room grid instead of leaving at a random spot
    document.querySelectorAll('.btn-buy-furniture').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const el = e.currentTarget as HTMLElement;
        const typeId = el.getAttribute('data-type')!;
        const tier = Number(el.getAttribute('data-tier') || '1');
        if (!DecorSystem.hasActiveDecorSession()) {
          DecorSystem.beginDecorSession();
        }
        const res = DecorSystem.placeFurniture(typeId, tier);
        if (res.success && res.item) {
          this.close();
          EventBus.emit('SELECT_FURNITURE_FOR_MOVE', res.item.id);
          ToastManager.getInstance().show(
            'Đã mua nội thất! Chạm vào ô xanh hoặc kéo để đặt vị trí bạn muốn.',
            'success'
          );
        } else {
          ToastManager.getInstance().show(res.error || 'Không thể mua!', 'warning');
        }
      });
    });

    // Place souvenir -> immediately enter Move Mode on the room grid
    document.querySelectorAll('.btn-place-souvenir').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const souvenirId = (e.currentTarget as HTMLElement).getAttribute('data-souvenir')!;
        if (!DecorSystem.hasActiveDecorSession()) {
          DecorSystem.beginDecorSession();
        }
        const res = DecorSystem.placeSouvenir(souvenirId);
        if (res.success && res.item) {
          this.close();
          EventBus.emit('SELECT_FURNITURE_FOR_MOVE', res.item.id);
          ToastManager.getInstance().show(
            'Chạm vào ô xanh hoặc kéo kỷ vật tới vị trí bạn muốn trưng bày!',
            'success'
          );
        } else {
          ToastManager.getInstance().show(res.error || 'Không thể đặt kỷ vật!', 'warning');
        }
      });
    });
  }
}
